/**
 * Accounting Types
 * TypeScript interfaces for the Accounting module (Chart of Accounts, Rules, Events, Entries)
 */

// ============================================================
// Enums / Type Literals
// ============================================================

export type AccountType = 'ASSET' | 'LIABILITY' | 'EQUITY' | 'REVENUE' | 'EXPENSE' | 'OTHER';

export type AccountingConcept =
  | 'NEW_TRADE'
  | 'VALUATION'
  | 'VALUATION_REVERSAL'
  | 'EXPIRY'
  | 'MODIFICATION_CANCELLED'
  | 'UNWIND';

export type AmountSource =
  | 'TRADE_AMOUNT_CCY1'
  | 'TRADE_AMOUNT_CCY2'
  | 'VALUATION_AMOUNT'
  | 'VALUATION_DELTA'
  | 'SETTLEMENT_AMOUNT'
  | 'CASHFLOW_AMOUNT'
  | 'CASHFLOW_CCY_AMOUNT'
  | 'SPOT_ELEMENT'
  | 'FORWARD_ELEMENT';

export type EntryMode = 'both' | 'debit_only' | 'credit_only';

export type NetSign = 'positive' | 'negative';

export type CurrencyHandling =
  | 'EVENT_CURRENCY'
  | 'ACCOUNT_CURRENCY'
  | 'FUNCTIONAL_CURRENCY'
  | 'VALUATION_CURRENCY';

export type DateFormat = 'DD/MM/YYYY' | 'YYYY-MM-DD' | 'MM/DD/YYYY';
export type NumberFormat = '1,234.56' | '1.234,56';
export type Delimiter = ',' | ';';
export type Encoding = 'UTF-8' | 'ISO-8859-1';

// ============================================================
// Export Settings
// ============================================================

export interface ExportColumn {
  field: string;
  name: string;
  order: number;
  included: boolean;
}

export interface ExportSettings {
  dateFormat: DateFormat;
  numberFormat: NumberFormat;
  delimiter: Delimiter;
  encoding: Encoding;
  columns: ExportColumn[];
}

// ============================================================
// Accounting Configuration
// ============================================================

export interface AccountingConfig {
  functionalCurrency: string;
  exportSettings: ExportSettings;
  lastUpdatedAt?: string;
  lastUpdatedBy?: string;
}

export interface AccountingConfigUpdate {
  functionalCurrency?: string;
  exportSettings?: ExportSettings;
}

// ============================================================
// Chart of Accounts
// ============================================================

export const MULTI_CURRENCY = 'MULTI';

export interface Account {
  id: string;
  accountNumber: string;
  name: string;
  description?: string;
  type: AccountType;
  currency: string;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  lastUpdatedAt?: string;
  lastUpdatedBy?: string;
}

export interface AccountCreate {
  accountNumber: string;
  name: string;
  description?: string;
  type: AccountType;
  currency: string;
}

export interface AccountUpdate {
  accountNumber?: string;
  name?: string;
  description?: string;
  currency?: string;
  isActive?: boolean;
}

// ============================================================
// Cost Centres
// ============================================================

export interface CostCentre {
  id: string;
  code: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  lastUpdatedAt?: string;
  lastUpdatedBy?: string;
}

export interface CostCentreCreate {
  code: string;
  description: string;
}

export interface CostCentreUpdate {
  code?: string;
  description?: string;
  isActive?: boolean;
}

// ============================================================
// Accounting Rules
// ============================================================

export type ConditionOperator = 'equals' | 'not_equals';

export interface RuleConditions {
  productType?: 'Spot' | 'Forward';
  productSubType?: string;
  settlementType?: 'Entrega Física' | 'Compensación';
  direction?: 'Buy' | 'Sell';
  currency1?: string;
  currency2?: string;
  cashflowCurrency?: string;
  anyCurrency?: string;
  amountSign?: 'positive' | 'negative';
  isInternalTrade?: boolean;
  isSplitTrade?: boolean;
  hasSplits?: boolean;
  ourPartyId?: string;
  grouping1?: string;
  grouping2?: string;
  grouping3?: string;
  // Condition operators (default: 'equals')
  ourPartyIdOperator?: ConditionOperator;
  currency1Operator?: ConditionOperator;
  currency2Operator?: ConditionOperator;
  anyCurrencyOperator?: ConditionOperator;
  grouping1Operator?: ConditionOperator;
  grouping2Operator?: ConditionOperator;
  grouping3Operator?: ConditionOperator;
}

export interface AccountingSplit {
  account: string;
  percentage: number;
  costCentre?: string;
}

export type TriggerState = 'confirmed' | 'booked_provisional';
export type AccountingDateSource = 'tradeDate' | 'valueDate';

export interface AccountingRule {
  id: string;
  name: string;
  description?: string;
  concept: AccountingConcept;
  conditions?: RuleConditions;
  debitAccount?: string;
  creditAccount?: string;
  debitCostCentre?: string;
  creditCostCentre?: string;
  debitSplits?: AccountingSplit[];
  creditSplits?: AccountingSplit[];
  amountSource: AmountSource;
  currencyHandling: CurrencyHandling;
  isActive: boolean;
  aggregated?: boolean;
  entryMode?: EntryMode;
  netSign?: NetSign;
  // Forward Starting timing (NEW_TRADE only)
  triggerState?: TriggerState;
  accountingDate?: AccountingDateSource;
  createdAt: string;
  createdBy: string;
  lastUpdatedAt?: string;
  lastUpdatedBy?: string;
}

export interface AccountingRuleCreate {
  name: string;
  description?: string;
  concept: AccountingConcept;
  conditions?: RuleConditions;
  debitAccount?: string;
  creditAccount?: string;
  debitCostCentre?: string;
  creditCostCentre?: string;
  debitSplits?: AccountingSplit[];
  creditSplits?: AccountingSplit[];
  amountSource: AmountSource;
  currencyHandling: CurrencyHandling;
  aggregated?: boolean;
  entryMode?: EntryMode;
  netSign?: NetSign;
  triggerState?: TriggerState;
  accountingDate?: AccountingDateSource;
}

export interface AccountingRuleUpdate {
  name?: string;
  description?: string;
  conditions?: RuleConditions;
  debitAccount?: string;
  creditAccount?: string;
  debitCostCentre?: string;
  creditCostCentre?: string;
  debitSplits?: AccountingSplit[];
  creditSplits?: AccountingSplit[];
  amountSource?: AmountSource;
  currencyHandling?: CurrencyHandling;
  isActive?: boolean;
  aggregated?: boolean;
  entryMode?: EntryMode;
  netSign?: NetSign;
  triggerState?: TriggerState;
  accountingDate?: AccountingDateSource;
}

// ============================================================
// Accounting Events
// ============================================================

export interface AccountingEvent {
  id: string;
  concept: AccountingConcept;
  eventDate: string;
  sourceType: 'TRADE' | 'VALUATION' | 'CASHFLOW';
  sourceId: string;
  tradeNumber: string;
  productType: 'Spot' | 'Forward';
  direction: 'Buy' | 'Sell';
  settlementType?: string;
  currency1: string;
  amount1: number;
  currency2?: string;
  amount2?: number;
  valuationAmount?: number;
  valuationCurrency?: string;
  previousValuationAmount?: number;
  settlementCurrency?: string;
  settlementAmount?: number;
  processed: boolean;
  generatedAt: string;
  batchId: string;
  leg?: 'CCY1' | 'CCY2';
}

// ============================================================
// Accounting Entries
// ============================================================

export interface AccountingEntry {
  id: string;
  eventId: string;
  ruleId: string;
  entryDate: string;
  concept: AccountingConcept;
  tradeNumber: string;
  accountNumber: string;
  accountName: string;
  costCentre?: string;
  debitAmount?: number;
  creditAmount?: number;
  currency: string;
  exchangeRate?: number;
  originalAmount?: number;
  originalCurrency?: string;
  description: string;
  generatedAt: string;
  batchId: string;
  aggregated?: boolean;
  matchedEventCount?: number;
}

// ============================================================
// Valuations
// ============================================================

/** Valuation snapshot embedded on a trade document */
export interface TradeValuation {
  mtmAmount: number;
  currency: string;
  valuationDate: string;
  uploadId: string;
  uploadedAt: string;
}

/** Upload log entry stored in valuationUploads collection */
export interface ValuationUpload {
  id: string;
  fileName: string;
  uploadedBy: string;
  uploadDate: string;
  tradeCount: number;
  status: 'processing' | 'complete' | 'partial' | 'error';
  errors: ValuationError[];
  createdAt: string;
}

/** Trade with its embedded valuation data (returned by latest-valuations endpoint) */
export interface TradeWithValuation {
  tradeId: string;
  tradeNumber: string;
  productType: string;
  direction: string;
  currency1: string;
  currency2: string;
  quantityCurrency1: number;
  currentValuation?: TradeValuation;
  previousValuation?: TradeValuation;
}

export interface ValuationUploadResult {
  success: boolean;
  totalRows: number;
  validRows: number;
  errors: ValuationError[];
  uploadId?: string;
}

export interface ValuationError {
  row: number;
  tradeNumber: string;
  error: string;
}

// ============================================================
// Generation Schedule
// ============================================================

export interface DailyScheduleConfig {
  enabled: boolean;
  concepts: AccountingConcept[];
  runTime: string;
  timezone: string;
}

export interface MonthlyScheduleConfig {
  enabled: boolean;
  concepts: AccountingConcept[];
  timing: 'last_business_day' | 'first_business_day_next';
  runTime: string;
  timezone: string;
}

export interface GenerationSchedule {
  daily: DailyScheduleConfig;
  monthly: MonthlyScheduleConfig;
}

// ============================================================
// Generation
// ============================================================

export interface GenerationRequest {
  dateFrom?: string;
  dateTo?: string;
  concepts?: AccountingConcept[];
  regenerateMode?: 'skip_processed' | 'delete_and_regenerate';
}

export interface GenerationResult {
  success: boolean;
  batchId: string;
  eventsGenerated: number;
  entriesGenerated: number;
  errors: GenerationError[];
  summary: {
    byConceptEvents: Record<AccountingConcept, number>;
    byConceptEntries: Record<AccountingConcept, number>;
  };
}

export interface GenerationError {
  eventId?: string;
  tradeNumber?: string;
  error: string;
}

export interface BatchRunReport {
  mode: 'daily' | 'monthly';
  systemDate: string;
  dateFrom: string;
  dateTo: string;
  concepts: AccountingConcept[];
  eventsGenerated: number;
  entriesGenerated: number;
  eventsErrors: number;
  entriesErrors: number;
  eventsSummary?: Record<string, any>;
  entriesSummary?: Record<string, any>;
}

export interface AccountingBatch {
  id: string;
  generatedAt: string;
  generatedBy: string;
  dateFrom?: string;
  dateTo?: string;
  concepts: AccountingConcept[];
  eventsGenerated: number;
  entriesGenerated: number;
  status: 'completed' | 'completed_with_errors' | 'failed';
  errors?: { concept: string; error: string }[];
  triggerSource?: 'manual' | 'scheduled_daily' | 'scheduled_monthly';
  runReport?: BatchRunReport;
}

// ============================================================
// Helper constants
// ============================================================

export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  ASSET: 'Assets',
  LIABILITY: 'Liabilities',
  EQUITY: 'Equity',
  REVENUE: 'Revenue',
  EXPENSE: 'Expenses',
  OTHER: 'Other',
};

export const ACCOUNT_TYPE_PREFIXES: Record<string, AccountType> = {
  '1': 'ASSET',
  '2': 'LIABILITY',
  '3': 'EQUITY',
  '4': 'REVENUE',
  '5': 'EXPENSE',
  '6': 'OTHER',
  '7': 'OTHER',
  '8': 'OTHER',
  '9': 'OTHER',
};

export const CONCEPT_LABELS: Record<AccountingConcept, string> = {
  NEW_TRADE: 'New Trade',
  VALUATION: 'Valuation',
  VALUATION_REVERSAL: 'Valuation Reversal',
  EXPIRY: 'Expiry',
  MODIFICATION_CANCELLED: 'Modification Cancelled',
  UNWIND: 'Unwind',
};
